import React, { useEffect, useState, useContext, useRef } from 'react';
import { 
  Container, Typography, Button, Paper, Snackbar, Alert, Box, Grid, 
  Divider, CircularProgress, IconButton, Tooltip, Card, CardContent, 
  CardActionArea, Chip, Avatar, useTheme, useMediaQuery, alpha,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import Layout from '../components/Layout';
import SubscriptionPanel from '../components/SubscriptionPanel';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import axios from 'axios';
import SettingsIcon from '@mui/icons-material/Settings';
import PeopleIcon from '@mui/icons-material/People';
import LayersIcon from '@mui/icons-material/Layers';
import VideoChatIcon from '@mui/icons-material/VideoChat';
import LogsIcon from '@mui/icons-material/Topic';
import PreviewIcon from '@mui/icons-material/Preview';
import PinIcon from '@mui/icons-material/Pin';
import FaceIcon from '@mui/icons-material/Face';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ContactsIcon from '@mui/icons-material/Contacts';
import UpgradeIcon from '@mui/icons-material/Upgrade';
import { AuthContext } from '../AuthContext';
import HelpIcon from '@mui/icons-material/Help';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import '../styles.css'; 
import { useNavigate, useLocation } from 'react-router-dom';
import { createIntroHighlights, removeIntroHighlights } from '../utils/introHighlight';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CodeIcon from '@mui/icons-material/Code';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export default function HomePage() {
    const [data, setData] = React.useState(null);
    const [credentials, setCredentials] = React.useState('', '');
    const [showPassword, setShowPassword] = useState(false);
    const [showCredentialsQr, setShowCredentialsQr] = useState(false);
    const [keepalive, setKeepalive] = useState(null); 
    const { globalTenantId } = useContext(AuthContext);
    const [subscriptionPlan, setSubscriptionPlan] = useState(null);
    const globalTenantIdRef = useRef(globalTenantId);
    const [subscriptionInfoLoading, setSubscriptionInfoLoading] = useState(true);
    const [kioskStatusLoading, setKioskStatusLoading] = useState(true);
    const [kioskCredentialsLoading, setKioskCredentialsLoading] = useState(true); 
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(AuthContext);
    const [showSubscriptionPanel, setShowSubscriptionPanel] = useState(false);
    const [usageData, setUsageData] = useState(null);
    const [usageLoading, setUsageLoading] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        globalTenantIdRef.current = globalTenantId;
    }, [globalTenantId]);
   
    // Callback to open a checkout
    const openCheckout = () => {
        navigate('/client-admin/upgrade'); 
    };

    const handlePreviewKiosk = () => {
        if (!globalTenantId) {
          console.log('No tenantid in context');
          return;
        }
  
        // show a popup with the kiosk in it
        let kioskUrl;
        axios.get('/api/setting?name=KIOSKURL')
        .then(result => {
            const data = result.data;
            kioskUrl = data.value + '?tenantId=' + globalTenantId;
            window.open(kioskUrl, 'kiosk', 'width=400,height=600');
          })
        .catch(error => console.error('Error fetching data:', error))
  
      };

    // Retrieve success message from the state
    useEffect(() => {
        if (location.state?.message) {
            setSuccessMessage(location.state.message);
            setShowSuccess(true);
        }
    }, [location]);

    const fetchPageData = () => {
        if (globalTenantId == null) return;
        
        setKioskStatusLoading(true);
        setKioskCredentialsLoading(true);
        axios.get(`/api/tenants/${globalTenantId}`, { params: { fields: ['OrganisationName'] } }).then(result => {
            setData(result.data);
        }).catch(error => {
            console.error('Error fetching data:', error)
        });
        axios.post('/api/kioskStatus', {}, { params: { tenantId: globalTenantId } }).then(result => {
            setKeepalive(result.data);
            setKioskStatusLoading(false);
          }).catch(error => {
            console.error('Error fetching kiosk status:', error);
          });
          
        axios.get('/api/kioskCredentials', { params: { tenantId: globalTenantId } }).then(result => {
            setCredentials(result.data);
            setKioskCredentialsLoading(false);
        }).catch(error => {
            console.error('Error fetching kiosk credentials:', error);
        });

        if (user.role == 'CLIENT_ADMIN') {
            setShowSubscriptionPanel(true);
            fetchSubscriptionData();
        }
    
    };

    const fetchSubscriptionData = () => {
        setSubscriptionInfoLoading(true);
        
        // Fetch subscription plan first for immediate display
        axios.get('/api/customer-subscription', { params: { tenantId: globalTenantIdRef.current } })
        .then(result => {
            const newPlan = JSON.parse(JSON.stringify(result.data));  // this must be done to force a re-render
            setSubscriptionPlan(newPlan);
            setSubscriptionInfoLoading(false);
            
            // Then fetch usage data in the background
            fetchUsageData();
        })
        .catch(error => {
            console.error('Error fetching subscription data:', error);
            setSubscriptionInfoLoading(false);
        });
    }

    const fetchUsageData = () => {
        setUsageLoading(true);
        
        axios.get('/api/customer-usage', { params: { tenantId: globalTenantIdRef.current } })
        .then(result => {
            setUsageData(result.data);
            setUsageLoading(false);
        })
        .catch(error => {
            console.error('Error fetching usage data:', error);
            setUsageLoading(false);
        });
    }

    useEffect(() => {
        fetchPageData();
    }, [globalTenantId]); 
    
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(
            () => { console.log('Copied to clipboard'); },
            (err) => { console.error('Could not copy text: ', err); }
        );
    };

    const handleKioskHelp = () => {
        window.open('https://www.entrinsic.io/installation-guide', '_blank');
    };

    // Navigation handlers for menu items
    const handleNavigateTo = (path) => {
        navigate(path);
    };

    // Define menu items for the dashboard
    const menuItems = [
        { icon: <LogsIcon />, title: 'Activity Logs', description: 'View activity logs and device status', onClick: () => handleNavigateTo('logs') },
        { icon: <LayersIcon />, title: 'Number Plates', description: 'Manage vehicle registration plates for ANPR access', onClick: () => handleNavigateTo('number-plates') },
        { icon: <PinIcon />, title: 'PIN Codes', description: 'Configure PIN access codes', onClick: () => handleNavigateTo('pins') },
        { icon: <FaceIcon />, title: 'Face Profiles', description: 'Manage face recognition profiles', onClick: () => handleNavigateTo('face-profiles') },
        { icon: <QrCodeIcon />, title: 'QR Codes', description: 'Generate and manage QR code keys', onClick: () => handleNavigateTo('qr-code-keys') },
        { icon: <VideoChatIcon />, title: 'Video Messages', description: 'Review visitor video messages', onClick: () => handleNavigateTo('video-messages') },
        { icon: <PhotoLibraryIcon />, title: 'Snapshots', description: 'View captured snapshots and movement detection', onClick: () => handleNavigateTo('snapshots') },
        { icon: <OndemandVideoIcon />, title: 'Movement Videos', description: 'View movement detection videos', onClick: () => handleNavigateTo('movement-videos') },
        { icon: <PreviewIcon />, title: 'Preview Kiosk', description: 'Preview kiosk interface', onClick: handlePreviewKiosk },
        { icon: <SettingsIcon />, title: 'Settings', description: 'Configure kiosk settings and behavior', onClick: () => handleNavigateTo('settings') },
        { icon: <CodeIcon />, title: 'API & Webhooks', description: 'Configure your developer account, webhooks and access API docs', onClick: () => handleNavigateTo('developers') },
    ];

    // Add admin-only menu items
    if (showSubscriptionPanel) {
        menuItems.push(
            { icon: <PeopleIcon />, title: 'User Management', description: 'Manage user access and responder permissions', onClick: () => handleNavigateTo('users') },
            { icon: <ContactsIcon />, title: 'Responder Aliases', description: 'Configure aliases for responder selection and groupings', onClick: () => handleNavigateTo('responder-aliases') }
        );
    }

    useEffect(() => {
        // Try to highlight after a short delay to ensure DOM is ready
        setTimeout(createIntroHighlights, 1000);

        // Cleanup
        return () => {
            removeIntroHighlights();
        };
    }, []);

    return (
        <Layout title="Entrinsic Connect Dashboard">
            <Box sx={{ 
                animation: 'fadeIn 0.5s ease-in-out',
                '@keyframes fadeIn': {
                    '0%': {
                        opacity: 0,
                        transform: 'translateY(10px)',
                    },
                    '100%': {
                        opacity: 1,
                        transform: 'translateY(0)',
                    },
                },
            }}>
                <Box sx={{ 
                    mb: 4, 
                    p: 3, 
                    borderRadius: 2, 
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.light, 0.2)} 100%)`,
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box>
                        <Typography variant="h5" fontWeight="600" color="primary.main">
                            {data ? data.OrganisationName : 'Loading...'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {isMobile
                                ? 'Use the menu in the top right corner to switch between devices.'
                                : 'Select from the options below to manage and configure your device. Use the menu in the top right corner to switch between devices.'}
                        </Typography>
                    </Box>
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        backgroundColor: 'background.paper', 
                        p: 1, 
                        borderRadius: 2,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)'
                    }}>
                        {kioskStatusLoading ? (
                            <CircularProgress size={24} sx={{ mr: 1 }} />
                        ) : (
                            <>
                                {!keepalive || keepalive > 2 ? (
                                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                                ) : (
                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                                )}
                            </>
                        )}
                        <Box>
                            <Typography variant="body2" fontWeight="500">
                                {kioskStatusLoading ? 'Checking status...' : 
                                    (!keepalive || keepalive > 2 ? 'Device Offline' : 'Device Online')}
                            </Typography>
                            {keepalive && !kioskStatusLoading && (
                                <Typography variant="caption" color="text.secondary">
                                    Last seen {keepalive.toFixed(1)} minute(s) ago
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>

                <Grid container spacing={3}>
                    {/* Main content area */}
                    <Grid item xs={12} lg={showSubscriptionPanel ? 8 : 12}>
                        {!isMobile && <Grid container spacing={2} sx={{ mb: 4 }}>
                            {menuItems.map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card 
                                        sx={{ 
                                            height: '100%',
                                            textDecoration: 'none',
                                            color: 'inherit',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)',
                                                '& .icon-wrapper': {
                                                    backgroundColor: theme.palette.primary.main,
                                                    color: 'white',
                                                }
                                            }
                                        }}
                                    >
                                        <CardActionArea 
                                            onClick={item.onClick}
                                            sx={{ 
                                                height: '100%', 
                                                p: 2,
                                                display: 'block',
                                                textDecoration: 'none',
                                                color: 'inherit'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                                <Box 
                                                    className="icon-wrapper"
                                                    sx={{ 
                                                        mr: 2, 
                                                        p: 1.5, 
                                                        borderRadius: 2, 
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                        color: theme.palette.primary.main,
                                                        transition: 'all 0.2s ease'
                                                    }}
                                                >
                                                    {React.cloneElement(item.icon, { fontSize: 'medium' })}
                                                </Box>
                                                <Box>
                                                    <Typography variant="subtitle1" fontWeight="600" gutterBottom>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {item.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardActionArea>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>}

                        {/* Kiosk login details card */}
                        <Card 
                            sx={{ 
                                mb: 3, 
                                borderRadius: 2, 
                                boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
                                overflow: 'visible'
                            }}
                            data-intro='Use these details to login to your device or kiosk. Click the help icon to find out more.'
                        >
                            <Box sx={{ 
                                p: 2, 
                                bgcolor: theme.palette.primary.main, 
                                color: 'white',
                                borderTopLeftRadius: 8,
                                borderTopRightRadius: 8,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                            }}>
                                <Typography variant="subtitle1" fontWeight="bold">
                                    LIVE KIOSK LOGIN DETAILS
                                </Typography>
                                <Tooltip title='Click here to find out how to set up your device'>
                                    <IconButton 
                                        onClick={handleKioskHelp} 
                                        size="small" 
                                        sx={{ 
                                            color: 'white',
                                            bgcolor: 'rgba(255, 255, 255, 0.2)',
                                            '&:hover': {
                                                bgcolor: 'rgba(255, 255, 255, 0.3)',
                                            }
                                        }}
                                    >
                                        <HelpIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <CardContent sx={{ p: 3 }}>
                                {kioskCredentialsLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                        <CircularProgress size={30} />
                                    </Box>
                                ) : (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ 
                                                p: 2, 
                                                borderRadius: 2, 
                                                bgcolor: alpha(theme.palette.primary.light, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Username
                                                    </Typography>
                                                    <Typography variant="body1" fontWeight="500">
                                                        {credentials.email || 'Loading...'}
                                                    </Typography>
                                                </Box>
                                                <IconButton 
                                                    onClick={() => copyToClipboard(credentials.email || '')}
                                                    size="small"
                                                    sx={{ 
                                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                        '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                                                        }
                                                    }}
                                                >
                                                    <FileCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <Box sx={{ 
                                                p: 2, 
                                                borderRadius: 2, 
                                                bgcolor: alpha(theme.palette.primary.light, 0.1),
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between'
                                            }}>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Password
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Typography variant="body1" fontWeight="500" sx={{ mr: 1 }}>
                                                            {showPassword ? credentials.password : '••••••'}
                                                        </Typography>
                                                        <IconButton 
                                                            onClick={togglePasswordVisibility} 
                                                            size="small"
                                                            sx={{ mr: 1 }}
                                                        >
                                                            {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                                {showPassword && (
                                                    <IconButton 
                                                        onClick={() => copyToClipboard(credentials.password || '')}
                                                        size="small"
                                                        sx={{ 
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                            '&:hover': {
                                                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                                            }
                                                        }}
                                                    >
                                                        <FileCopyIcon fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<QrCodeIcon />}
                                                onClick={() => setShowCredentialsQr(true)}
                                                disabled={!credentials.email || !credentials.password}
                                            >
                                                Show QR code
                                            </Button>
                                        </Grid>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Subscription panel */}
                    {showSubscriptionPanel && (
                        <Grid item xs={12} lg={4}>
                            <SubscriptionPanel
                                subscriptionPlan={subscriptionPlan}
                                usageData={usageData}
                                usageLoading={usageLoading}
                                subscriptionInfoLoading={subscriptionInfoLoading}
                                onUpgradeClick={openCheckout}
                                user={user}
                            />
                        </Grid>
                    )}
                </Grid>
            </Box>
            
            {/* Format must match KioskCredentialsQr.parse in the Kiosk Manager app */}
            <Dialog open={showCredentialsQr} onClose={() => setShowCredentialsQr(false)}>
                <DialogTitle>Kiosk login QR code</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                        On the kiosk, open Kiosk Manager settings, go to Portal Credentials and tap "Scan QR code".
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 2 }}>
                        <QRCodeSVG
                            value={JSON.stringify({ type: 'entrinsic-kiosk-login', v: 1, username: credentials.email, password: credentials.password })}
                            size={256}
                            level="M"
                            marginSize={2}
                        />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        This code contains the kiosk's password. Don't share it.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowCredentialsQr(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Success message snackbar */}
            <Snackbar
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                open={showSuccess}
                autoHideDuration={5000}
                onClose={() => setShowSuccess(false)}
            >
                <Alert 
                    severity="success" 
                    onClose={() => setShowSuccess(false)}
                    sx={{ 
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                        borderRadius: 2
                    }}
                >
                    {successMessage}
                </Alert>
            </Snackbar>
        </Layout>
    );
}